import { Ship, Truck } from "lucide-react";
import type React from "react";
import { useState } from "react";

/** True once, computed on mount — whether the OS asked for reduced motion. */
function usePrefersReducedMotion(): boolean {
  const [reduced] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  return reduced;
}

export type BackdropVariant = "immersive" | "calm";

/**
 * TrainingBackdrop — the themed atmosphere behind the Training section.
 *
 * variant="immersive" (overview screen only):
 *   Dark:  a navy "trade command center" — a stylized world dot-map with
 *          glowing animated great-circle routes between Asia / US / Mexico.
 *   Light: a cream "blueprint" — a fine technical grid, corner registration
 *          marks, and a rotated customs-stamp motif.
 *
 * variant="calm" (lesson screens):
 *   A quiet, static backdrop — no motion, no dense grid — so reading and
 *   quizzes stay the focus. A single large, very faint truck + ship
 *   watermark keeps the logistics theme present without competing for
 *   attention.
 *
 * Purely decorative (aria-hidden), sits behind content via a lower z-index.
 * Respects prefers-reduced-motion by disabling the flowing-route animation.
 */
export default function TrainingBackdrop({
  variant = "immersive",
}: {
  variant?: BackdropVariant;
}): React.JSX.Element {
  if (variant === "calm") {
    return <CalmBackdrop />;
  }
  return <ImmersiveBackdrop />;
}

// ── Immersive (overview) ─────────────────────────────────────────────────────

function ImmersiveBackdrop(): React.JSX.Element {
  const reducedMotion = usePrefersReducedMotion();
  return (
    <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden">
      {/* ── Dark: trade-route command center ── */}
      <div className="hidden dark:block absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 120% 80% at 30% -10%, #16213f 0%, #0b1220 55%, #070b16 100%)",
          }}
        />
        {/* faint dot grid */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(148,163,209,0.18) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 900 500"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
        >
          <defs>
            <filter id="td-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <radialGradient id="td-node-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Stylized world dot-map: loose continent silhouettes as dot clusters */}
          <g fill="rgba(148,163,209,0.35)">
            {WORLD_DOTS.map((d, i) => (
              <circle key={i} cx={d[0]} cy={d[1]} r={1.6} />
            ))}
          </g>

          {/* Trade routes: Asia ↔ USA ↔ Mexico, animated dash flow */}
          {ROUTES.map((r, i) => (
            <path
              key={i}
              d={r}
              stroke="#38bdf8"
              strokeWidth={1.4}
              strokeLinecap="round"
              opacity={0.55}
              filter="url(#td-glow)"
              strokeDasharray="2 10"
              className="td-route-flow"
            />
          ))}

          {/* Hub nodes */}
          {HUBS.map(([x, y], i) => (
            <g key={i}>
              <circle cx={x} cy={y} r={14} fill="url(#td-node-glow)" className="td-node-pulse" />
              <circle cx={x} cy={y} r={3} fill="#7dd3fc" />
            </g>
          ))}

          {/* Moving vehicles: a plane, a ship, and a truck tracing the trade routes */}
          {!reducedMotion &&
            VEHICLES.map((v, i) => (
              <g
                key={i}
                fill="none"
                stroke="#bae6fd"
                strokeWidth={1.3}
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="url(#td-glow)"
              >
                <VehicleGlyph kind={v.kind} />
                <animateMotion
                  dur={`${v.duration}s`}
                  repeatCount="indefinite"
                  rotate="auto"
                  path={v.route}
                  begin={`${v.delay}s`}
                />
                <animate
                  attributeName="opacity"
                  values="0;1;1;0"
                  keyTimes="0;0.06;0.9;1"
                  dur={`${v.duration}s`}
                  repeatCount="indefinite"
                  begin={`${v.delay}s`}
                />
              </g>
            ))}
        </svg>
      </div>

      {/* ── Light: blueprint / customs-stamp ── */}
      <div className="dark:hidden absolute inset-0">
        <div className="absolute inset-0" style={{ background: "#f7f4ec" }} />
        {/* fine blueprint grid */}
        <div
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage:
              "linear-gradient(rgba(96,120,158,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(96,120,158,0.12) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "linear-gradient(rgba(96,120,158,0.22) 1px, transparent 1px), linear-gradient(90deg, rgba(96,120,158,0.22) 1px, transparent 1px)",
            backgroundSize: "140px 140px",
          }}
        />
        {/* corner registration marks */}
        {CORNERS.map((c, i) => (
          <svg key={i} className={`absolute w-14 h-14 ${c.pos}`} viewBox="0 0 56 56" fill="none">
            <path d={c.path} stroke="#5b7ba8" strokeWidth="1.5" opacity={0.55} />
            <circle cx={c.dot[0]} cy={c.dot[1]} r="2" fill="#5b7ba8" opacity={0.55} />
          </svg>
        ))}
        {/* customs-stamp motif */}
        <svg
          className="absolute top-10 right-10 w-32 h-32 opacity-[0.18] -rotate-12 hidden sm:block"
          viewBox="0 0 120 120"
          fill="none"
        >
          <circle cx="60" cy="60" r="52" stroke="#8a3b2e" strokeWidth="2.5" strokeDasharray="4 3" />
          <circle cx="60" cy="60" r="40" stroke="#8a3b2e" strokeWidth="1.5" />
          <text
            x="60"
            y="56"
            textAnchor="middle"
            fontSize="10"
            fontWeight="700"
            fill="#8a3b2e"
            letterSpacing="1"
          >
            ADUANA
          </text>
          <text
            x="60"
            y="70"
            textAnchor="middle"
            fontSize="8"
            fontWeight="600"
            fill="#8a3b2e"
            letterSpacing="2"
          >
            MEXICO
          </text>
        </svg>

        {/* Trade routes + moving vehicles, redrawn in blueprint ink so they read on cream */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 900 500"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
        >
          <g fill="rgba(91,123,168,0.3)">
            {WORLD_DOTS.map((d, i) => (
              <circle key={i} cx={d[0]} cy={d[1]} r={1.6} />
            ))}
          </g>

          {ROUTES.map((r, i) => (
            <path
              key={i}
              d={r}
              stroke="#5b7ba8"
              strokeWidth={1.4}
              strokeLinecap="round"
              opacity={0.5}
              strokeDasharray="2 10"
              className="td-route-flow"
            />
          ))}

          {HUBS.map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r={3} fill="#5b7ba8" opacity={0.6} />
          ))}

          {!reducedMotion &&
            VEHICLES.map((v, i) => (
              <g
                key={i}
                fill="none"
                stroke="#3b5578"
                strokeWidth={1.3}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={0.65}
              >
                <VehicleGlyph kind={v.kind} />
                <animateMotion
                  dur={`${v.duration}s`}
                  repeatCount="indefinite"
                  rotate="auto"
                  path={v.route}
                  begin={`${v.delay}s`}
                />
                <animate
                  attributeName="opacity"
                  values="0;0.65;0.65;0"
                  keyTimes="0;0.06;0.9;1"
                  dur={`${v.duration}s`}
                  repeatCount="indefinite"
                  begin={`${v.delay}s`}
                />
              </g>
            ))}
        </svg>
      </div>
    </div>
  );
}

// ── Calm (lesson reading) ────────────────────────────────────────────────────

function CalmBackdrop(): React.JSX.Element {
  return (
    <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden">
      {/* ── Dark: quiet navy, no motion ── */}
      <div className="hidden dark:block absolute inset-0">
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, #0d1526 0%, #0a1120 100%)" }}
        />
        <Truck
          className="absolute -bottom-10 -right-10 w-72 h-72 text-sky-200 opacity-[0.045] rotate-[-6deg]"
          strokeWidth={1}
        />
        <Ship
          className="absolute top-16 -left-8 w-48 h-48 text-sky-200 opacity-[0.035] rotate-[8deg] hidden sm:block"
          strokeWidth={1}
        />
      </div>

      {/* ── Light: quiet cream, no motion ── */}
      <div className="dark:hidden absolute inset-0">
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, #f8f6f0 0%, #f3f0e8 100%)" }}
        />
        <Truck
          className="absolute -bottom-10 -right-10 w-72 h-72 text-slate-500 opacity-[0.06] rotate-[-6deg]"
          strokeWidth={1}
        />
        <Ship
          className="absolute top-16 -left-8 w-48 h-48 text-slate-500 opacity-[0.05] rotate-[8deg] hidden sm:block"
          strokeWidth={1}
        />
      </div>
    </div>
  );
}

// ── Data: loose dot-matrix world silhouette (viewBox 900×500) ────────────────
// Not geographically precise — a stylized decorative approximation.
//
// The whole map/route/hub set is deliberately compressed into the TOP ~26% of
// the viewBox (y ≈ 13–130 of 500), regardless of the wide banner it was first
// sketched at. Content that sits further down risks drifting into whatever a
// consuming page anchors at the bottom (e.g. the login panel's headline) once
// "xMidYMid slice" fits it to a tall/narrow container. Keep new points within
// that same band — coordinates are compressed here rather than via an SVG
// transform so dots stay circular and strokes stay their authored width.
const WORLD_DOTS: Array<[number, number]> = [
  // North America
  [120, 46],
  [140, 42],
  [160, 44],
  [180, 48],
  [150, 55],
  [170, 59],
  [190, 57],
  [130, 63],
  [160, 69],
  [185, 72],
  [145, 76],
  [175, 80],
  [155, 82],
  // Central/South America
  [175, 88],
  [185, 95],
  [195, 101],
  [190, 109],
  [200, 116],
  [210, 122],
  [205, 130],
  // Europe
  [430, 42],
  [450, 40],
  [470, 44],
  [445, 48],
  [460, 51],
  // Africa
  [440, 72],
  [455, 78],
  [470, 84],
  [450, 90],
  [465, 97],
  [440, 103],
  [460, 109],
  // Asia
  [560, 46],
  [590, 44],
  [620, 48],
  [650, 42],
  [600, 55],
  [630, 59],
  [660, 55],
  [600, 63],
  [640, 67],
  [670, 63],
  [610, 72],
  [650, 76],
  [590, 69],
  // SE Asia / Oceania
  [630, 93],
  [650, 99],
  [670, 103],
  [690, 109],
  [705, 116],
];

// Great-circle-ish bezier arcs between Asia hub, USA hub, Mexico hub.
const ROUTES = [
  "M 620 51 C 480 13, 300 13, 165 46", // Asia → USA
  "M 620 51 C 500 88, 320 84, 180 82", // Asia → Mexico
  "M 165 46 C 168 63, 175 72, 180 82", // USA → Mexico
];

const HUBS: Array<[number, number]> = [
  [620, 51], // Asia
  [165, 46], // USA
  [180, 82], // Mexico
];

type VehicleKind = "plane" | "ship" | "truck";

/** Small line-art glyphs, drawn nose/bow-forward along +x — animateMotion's rotate="auto" then orients them along the path's direction of travel. */
function VehicleGlyph({ kind }: { kind: VehicleKind }): React.JSX.Element {
  switch (kind) {
    case "plane":
      return <path d="M 7 0 L -5 -4 L -1 0 L -5 4 Z" />;
    case "ship":
      // A simple hull silhouette, pointed bow at +x, flat stern at -x — no mast,
      // since rotate="auto" spins the whole glyph to match travel direction and
      // a vertical mast reads as a stray handle once rotated off-vertical.
      return <path d="M 8 1 L 6 3 C 4 4 0 4 -4 4 L -7 4 L -7 1 Z" />;
    case "truck":
      return (
        <>
          <path d="M -8 4 H 8 V -3 H 2 L -1 -3 H -8 Z" />
          <circle cx="-5" cy="4.5" r="1.3" fill="#bae6fd" stroke="none" />
          <circle cx="4" cy="4.5" r="1.3" fill="#bae6fd" stroke="none" />
        </>
      );
  }
}

// Vehicles ride the same three arcs as ROUTES: plane over the long Asia↔USA
// air lane, ship on the slower Asia↔Mexico ocean lane, truck shuttling the
// short USA↔Mexico border crossing.
const VEHICLES: Array<{ kind: VehicleKind; route: string; duration: number; delay: number }> = [
  { kind: "plane", route: ROUTES[0]!, duration: 7, delay: 0 },
  { kind: "ship", route: ROUTES[1]!, duration: 13, delay: 2 },
  { kind: "truck", route: ROUTES[2]!, duration: 5, delay: 1 },
];

const CORNERS = [
  { pos: "top-2 left-2", path: "M2 20 L2 2 L20 2", dot: [2, 2] as [number, number] },
  { pos: "top-2 right-2 -scale-x-100", path: "M2 20 L2 2 L20 2", dot: [2, 2] as [number, number] },
  {
    pos: "bottom-2 left-2 -scale-y-100",
    path: "M2 20 L2 2 L20 2",
    dot: [2, 2] as [number, number],
  },
  {
    pos: "bottom-2 right-2 -scale-x-100 -scale-y-100",
    path: "M2 20 L2 2 L20 2",
    dot: [2, 2] as [number, number],
  },
];
